import type { GtiFormState } from '../gtiFormData'
import {
  GTI_BMP_CODE,
  GTI_CMP_CODE,
  GTI_PANEL_COMPONENTS,
  GTI_SUB_PANEL_CATEGORY,
  gtiSubPanelTestName,
  isGtiSubPanel,
} from '../gtiFormData'
import { LAB_ACCOUNT_SYSTEMS } from '../formData'
import type { OrderPdfData, OrderPdfTest, OrderPdfSection, AutoPopulateData } from './pdfTypes'
import type { CatalogTest } from '../shared/useTestCatalog'

/** The three groups GTI's form keeps apart, in the order it prints them. */
const CATEGORY_ORDER = ['Blood Test Panels', GTI_SUB_PANEL_CATEGORY]

function categoryRank(category?: string): number {
  const index = CATEGORY_ORDER.indexOf(category ?? '')
  // Anything else — the individual tests — runs last.
  return index === -1 ? CATEGORY_ORDER.length : index
}

export function mapGtiToPdf(
  form: GtiFormState,
  auto: AutoPopulateData,
  testMap?: Map<string, CatalogTest>,
): OrderPdfData {
  // PD-1529 — GTI publishes no order codes, so nothing here carries one: the
  // requisition names the tests, exactly as GTI's own form does.
  const seen = new Set<string>()
  const selectedTests: OrderPdfTest[] = []
  for (const code of form.selectedTests) {
    if (seen.has(code)) continue
    seen.add(code)
    const test = testMap?.get(code)
    // GTI's own form prints a panel's tests inside its box, one per line, so
    // the requisition carries them the same way — under the panel, never as
    // ordered rows of their own.
    const components = GTI_PANEL_COMPONENTS[code]
    selectedTests.push({
      // The catalog first, then the form's own wording. Never the bare code: a
      // failed catalog request would otherwise print an internal slug that GTI
      // cannot process.
      name: test?.name || gtiSubPanelTestName(code) || code,
      category: isGtiSubPanel(code)
        ? GTI_SUB_PANEL_CATEGORY
        : test?.categoryDisplay || test?.category || 'Test',
      ...(test?.specimen ? { specimen: test.specimen } : {}),
      ...(components ? { components } : {}),
    })
  }
  // GTI's form keeps its three groups apart, so the requisition runs in the
  // same order rather than in whatever order the boxes happened to be ticked.
  selectedTests.sort((a, b) => categoryRank(a.category) - categoryRank(b.category))

  const vendorSections: OrderPdfSection[] = []

  // Instructions for the draw, not a record of it: GTI's collector fills in the
  // collection date, time, tubes and their own name at the draw.
  const instructions: Record<string, string> = {}
  if (form.isStat) instructions['STAT'] = 'Yes'
  if (form.isFasting) instructions['Fasting Required'] = 'Yes'
  if (form.isVenipuncture) instructions['Venipuncture'] = 'Yes'
  if (Object.keys(instructions).length > 0) {
    vendorSections.push({
      title: 'Collection Instructions',
      type: 'keyValue',
      data: instructions,
    })
  }

  // GTI's requisition footnote. Said on the order itself so the ordering
  // provider isn't left waiting on a BMP result that will never come back.
  if (seen.has(GTI_CMP_CODE) && seen.has(GTI_BMP_CODE)) {
    vendorSections.push({
      title: 'Note',
      type: 'text',
      content:
        'CMP and BMP were both selected. GTI Laboratories will only process CMP, per their requisition.',
    })
  }

  const icd10Codes = form.diagnosisText
    ? form.diagnosisText
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  return {
    vendorName: 'GTI (Genetic Technological Innovations)',
    orderTitle: 'Test Requisition for Blood',
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      email: auto.patient.email,
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: auto.patient.patientId,
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
      accountNumber: auto.provider.labAccountNumbers[LAB_ACCOUNT_SYSTEMS.GTI] || '',
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.primaryCarrier ? auto.insurance.policyholderName : '',
      relationship: auto.insurance.primaryCarrier ? auto.insurance.relationship : '',
    },
    icd10Codes,
    selectedTests,
    // Panels, composite tests and plain tests are hard to tell apart in one flat
    // table, and GTI's own form separates them too.
    groupTestsByCategory: true,
    // GTI's acceptance criteria require patient contact info, and validation
    // lets an email alone satisfy that — so it has to be on the document.
    includePatientEmail: true,
    vendorSections,
    signatureDataUrl: form.signatureDataUrl,
    signatureDate: form.signatureDate,
  }
}
